import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import PopUpConfirmDeleteDraft from '@/pages/profile/drafts/components/PopUpConfirmDeleteDraft';
import GalleryService from '@/services/gallery.service';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles/QueuedArticlesPopup.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

function formatQueueDate(isoString) {
  if (!isoString) return '—';
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return '—';

  // Always display the schedule using the Madrid timezone selected in the form.
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: MADRID_TIMEZONE
  }).format(parsed);
}

export default function QueuedArticlesPopup({ open, onClose, articles }) {
  const t = useTranslations('common.profile.popups.queued_articles');
  const navigate = useNavigate();
  const { cacheHandler } = useStateHandler();
  const [pendingArticle, setPendingArticle] = useState(null);
  const [removedIds, setRemovedIds] = useState([]);

  const handleBackgroundClick = useCallback(event => {
    const card = event.currentTarget.querySelector('.generic-popup__card');
    if (card && card.contains(event.target)) {
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  const sortedArticles = useMemo(() => {
    // Keep upcoming publications in chronological order for quick scanning.
    return [...articles].sort((a, b) => {
      const aDate = Date.parse(a?.publishedAt ?? a?.post_container?.publishedAt ?? '');
      const bDate = Date.parse(b?.publishedAt ?? b?.post_container?.publishedAt ?? '');
      return aDate - bDate;
    });
  }, [articles]);

  useEffect(() => {
    if (removedIds.length === 0) return;
    const activeIds = new Set(articles.map(item => item._id));
    setRemovedIds(prev => {
      const next = prev.filter(id => activeIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [articles, removedIds]);

  const visibleArticles = useMemo(() => {
    if (removedIds.length === 0) return sortedArticles;
    const removedSet = new Set(removedIds);
    return sortedArticles.filter(item => !removedSet.has(item._id));
  }, [sortedArticles, removedIds]);

  const handleRemoveClick = useCallback((event, article) => {
    event?.stopPropagation?.();
    setPendingArticle(article);
  }, []);

  const handleItemOpen = useCallback(article => {
    if (!article?._id) return;

    const destination = `/profile/edit-article/${article._id}`;

    if (typeof onClose === 'function') {
      onClose();
    }

    navigate(destination, {
      state: { postData: article }
    });
  }, [navigate, onClose]);

  const handleConfirmDelete = useCallback(event => {
    event?.stopPropagation?.();
    if (!pendingArticle) return;

    const articleId = pendingArticle._id;
    setRemovedIds(prev => (prev.includes(articleId) ? prev : [...prev, articleId]));

    GalleryService.removePostDraft(articleId);
    cacheHandler.triggerAction('DELETE_POST');
  }, [pendingArticle, cacheHandler]);

  const pendingArticleTitle = pendingArticle?.post_container?.post_title || t('fallback.pending_article');

  return (
    <GenericPopup
      open={open}
      className="queued-articles-popup remove-scrollbar"
      darkerBlur
      opacity
      onBackgroundClick={handleBackgroundClick}
    >
      <div className="queued-articles-popup__header">
        <Text className="queued-articles-popup__title font-size-large">
          {t('title')}
        </Text>
        <XIcon className="queued-articles-popup__close" onClick={onClose} />
      </div>
      <div className="queued-articles-popup__list">
        {visibleArticles.length === 0 ? (
          <Text className="queued-articles-popup__empty font-size-small">
            {t('empty')}
          </Text>
        ) : (
          visibleArticles.map(item => {
            const title = item?.post_container?.post_title || t('fallback.untitled_article');
            const scheduled = item?.publishedAt ?? item?.post_container?.publishedAt;
            return (
              <div
                key={item._id}
                className="queued-articles-popup__item element-non-selectable"
                role="button"
                tabIndex={0}
                onClick={() => handleItemOpen(item)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleItemOpen(item);
                  }
                }}
              >
                <button
                  type="button"
                  className="queued-articles__remove-button"
                  aria-label={t('aria.remove_queued_article')}
                  onClick={event => handleRemoveClick(event, item)}
                >
                  <span />
                </button>
                <Text
                  className="queued-articles-popup__item-title dots-on-overflow font-size-medium">
                  {title}
                </Text>
                <Text className="queued-articles-popup__item-date font-size-small">
                  {formatQueueDate(scheduled)}
                </Text>
              </div>
            );
          })
        )}
      </div>
      {pendingArticle && (
        <PopUpConfirmDeleteDraft
          open
          close={() => setPendingArticle(null)}
          draftName={pendingArticleTitle}
          onConfirm={handleConfirmDelete}
          title={t('delete_dialog.title')}
        />
      )}
    </GenericPopup>
  );
}

QueuedArticlesPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  articles: PropTypes.arrayOf(PropTypes.object)
};

QueuedArticlesPopup.defaultProps = {
  open: false,
  onClose: undefined,
  articles: []
};
